import React, { useEffect, useState, useMemo, useRef } from "react";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import { Button, Tooltip } from "../..";
import {
  formatTimestampToAgo,
  getHostLogoStyleByHostId,
  socialPublishStatusToTextAndColor,
  copyTextToClipboard,
  getSocialChannelImage,
} from "../../../utils";
import SandboxIcon from "../../../assets/icons/svg/hourglass.svg?react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import { getPlatformIcon } from "@/utils/utilFunctions/commmonFunctions";
import { MediaModel } from "@/database";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import { getLiveUrl, getLivePreview } from "@/store/media/media.action";
import { LiveUrlItem, LivePreviewItem } from "@/database";

const LIVE_URL_HOSTS = [83, 19, 153];

interface DenmarkPublishPopoverProps {
  chip: DenmarkPublishInterface;
  anchor: DOMRect;
  hostLogoStyle?: React.CSSProperties;
  onClose: () => void;
  handleRouting: any;
  publishData: DenmarkPublishInterface;
  currentMediaDetailsForEdit?: MediaModel;
  tab?: string;
  left?: number;
  hosts?: any[];
  hostOptions?: any[];
  denmarkHosts?: Array<{
    hostId?: number | string;
    hostName?: string | null;
    denmarkActive?: boolean;
  }>;
  onEdit?: (publishData: DenmarkPublishInterface) => void;
  onAddToPriority?: (publishData: DenmarkPublishInterface) => void;
  showActions?: boolean;
  zIndex?: number;
}

const DenmarkPublishPopover: React.FC<DenmarkPublishPopoverProps> = ({
  anchor,
  onClose,
  handleRouting,
  publishData,
  currentMediaDetailsForEdit,
  left,
  hosts,
  hostOptions,
  denmarkHosts: denmarkHostsProp,
  onEdit,
  onAddToPriority,
  showActions = false,
  zIndex = 1050,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const searchParams = useSearchParams();
  const [localPublishData, setLocalPublishData] =
    useState<DenmarkPublishInterface>(publishData);
  const [liveRedirectUrl, setLiveRedirectUrl] = useState<string | null>(null);
  const [isLiveUrlFetching, setIsLiveUrlFetching] = useState(false);
  const initialPublishDataRef = useRef(publishData);
  const popoverRef = useRef<HTMLDivElement>(null);
  const denmarkHostsFromRedux = useSelector((state: RootState) => {
    const list = state.constants.allHostsList.list as any;
    if (list && Array.isArray(list.hosts)) {
      return list.hosts as Array<{
        hostId?: number | string;
        hostName?: string | null;
        denmarkActive?: boolean;
      }>;
    }
    if (Array.isArray(list)) {
      return list as Array<{
        hostId?: number | string;
        hostName?: string | null;
        denmarkActive?: boolean;
      }>;
    }
    return [] as Array<{
      hostId?: number | string;
      hostName?: string | null;
      denmarkActive?: boolean;
    }>;
  });

  // Use prop if provided, otherwise use Redux
  const denmarkHosts = denmarkHostsProp || denmarkHostsFromRedux;

  const socialChannelsList = useSelector(
    (state: RootState) => state.app.userSocialChannels.socialChannelsList
  );

  const getCleanParams = () => {
    const cleanParams = new URLSearchParams(searchParams[0]);
    return cleanParams.toString();
  };

  //syncing of data with currentmedia details for edit
  useEffect(() => {
    if (currentMediaDetailsForEdit?.socials && publishData?.id) {
      const matchingSocial = currentMediaDetailsForEdit.socials.find(
        (social: any) => social.id === publishData.id
      );
      if (matchingSocial) {
        setLocalPublishData((prev) => ({
          ...prev,
          status:
            matchingSocial.status !== undefined
              ? matchingSocial.status
              : prev.status,
          title: matchingSocial.title || prev.title,
          mt:
            matchingSocial.mt && matchingSocial.mt > 0
              ? matchingSocial.mt
              : prev.mt && prev.mt > 0
                ? prev.mt
                : Date.now(),
          ct:
            matchingSocial.ct && matchingSocial.ct > 0
              ? matchingSocial.ct
              : prev.ct && prev.ct > 0
                ? prev.ct
                : Date.now(),
        }));
      }
    }
  }, [
    currentMediaDetailsForEdit?.socials,
    publishData?.id,
    publishData?.published_id,
    publishData?.section,
    publishData?.job_type,
  ]);

  useEffect(() => {
    const hasChanged =
      publishData?.id !== initialPublishDataRef.current?.id ||
      publishData?.published_id !== initialPublishDataRef.current?.published_id;

    if (hasChanged) {
      setLocalPublishData(publishData);
      initialPublishDataRef.current = publishData;
    }
  }, [publishData?.id, publishData?.published_id]);

  const displayData = useMemo(() => localPublishData, [localPublishData]);

  const resolvedSocialImage = useMemo(
    () => getSocialChannelImage(displayData, socialChannelsList, "large"),
    [socialChannelsList, displayData]
  );

  /** Matches minWidth/maxWidth below so horizontal position stays within the viewport */
  const computedLeft = useMemo(() => {
    const viewportMargin = 8;
    const popoverMaxWidth = 300;
    const estimatedWidth = Math.min(
      popoverMaxWidth,
      Math.max(180, anchor.width, 270)
    );
    const preferred = left !== undefined ? left : anchor.left - 45;
    const minLeft = viewportMargin;
    const maxLeft = Math.max(
      minLeft,
      window.innerWidth - viewportMargin - estimatedWidth
    );
    return Math.min(Math.max(preferred, minLeft), maxLeft);
  }, [anchor.left, anchor.width, left]);

  const handleCopyId = async (e: any, id: any) => {
    e.preventDefault();
    e.stopPropagation();
    await copyTextToClipboard(id);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onEdit(displayData);
  };

  const handleAddToPriority = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToPriority) {
      onAddToPriority(displayData);
    }
  };

  useEffect(() => {
    if (
      displayData.job_type !== "denmark" ||
      displayData.status !== 10 ||
      !displayData.published_id ||
      !displayData.host
    ) {
      return;
    }

    const msid = String(displayData.published_id);
    const hostid = String(displayData.host);
    const hostNum = Number(displayData.host);

    setIsLiveUrlFetching(true);

    if (LIVE_URL_HOSTS.includes(hostNum)) {
      dispatch(getLiveUrl({ msid, hostid }))
        .then((res: { data: LiveUrlItem | null }) => {
          const url = res?.data?.url;
          if (url) setLiveRedirectUrl(url);
        })
        .finally(() => setIsLiveUrlFetching(false));
    } else {
      dispatch(getLivePreview({ msid, hostid }))
        .then((res: { data: LivePreviewItem | null }) => {
          const webUrl = res?.data?.liveUrls?.find(
            (u: LivePreviewItem["liveUrls"][number]) => u.platform === "Web"
          )?.url;
          if (webUrl) setLiveRedirectUrl(webUrl);
        })
        .finally(() => setIsLiveUrlFetching(false));
    }
  }, [
    dispatch,
    displayData.job_type,
    displayData.status,
    displayData.host,
    displayData.published_id,
  ]);

  const handleRedirect = () => {
    if (
      publishData.platform === "denmark" &&
      !location.pathname.includes("/details/")
    ) {
      navigate(
        `/media/${currentMediaDetailsForEdit?.id}/details?${getCleanParams()}`
      );
    }
    navigate(
      `/media/${
        currentMediaDetailsForEdit?.id
      }/social-publish?${getCleanParams()}`
    );
  };

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const popoverElement = popoverRef.current;
      if (popoverElement && !popoverElement.contains(target)) {
        onClose();
      }
    };

    const scrollHandler = () => {
      onClose();
    };

    window.addEventListener("mousedown", clickHandler);
    window.addEventListener("scroll", scrollHandler, true); // true for capturing phase

    return () => {
      window.removeEventListener("mousedown", clickHandler);
      window.removeEventListener("scroll", scrollHandler, true);
    };
  }, [onClose]);

  return (
    <div
      ref={popoverRef}
      data-popover
      className="bg-white border rounded p-3 position-fixed"
      style={{
        left: computedLeft,
        top:
          displayData.status === 10 && showActions
            ? "published_id" in (displayData ?? {})
              ? displayData.published_id !== undefined
                ? anchor.top - 140 // key present + has value + buttons
                : anchor.top - 130 // key present but value undefined + buttons
              : anchor.top - 140 // key not present at all + buttons
            : "published_id" in (displayData ?? {})
              ? displayData.published_id !== undefined
                ? anchor.top - 105 // key present + has value
                : anchor.top - 95 // key present but value undefined
              : anchor.top - 80, // key not present at all
        minWidth: Math.max(180, anchor.width, 270),
        maxWidth: 300,
        zIndex,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      <div className="d-flex align-items-start gap-2 position-relative">
        {(() => {
          const logoStyle = getHostLogoStyleByHostId(
            displayData.host,
            hosts,
            hostOptions,
            denmarkHosts
          );
          return logoStyle && Object.keys(logoStyle).length > 0 ? (
            <div className="position-relative">
              <div
                style={{
                  ...logoStyle,
                  height: "50px",
                  width: "50px",
                  borderRadius: "4px",
                  // border: "1px solid #dee2e6",
                }}
              />
            </div>
          ) : null;
        })()}
        <div className="d-flex align-items-start gap-3 flex-grow-1">
          {resolvedSocialImage && (
            <div className="position-relative">
              <img
                src={resolvedSocialImage}
                alt=""
                height="40px"
                width="40px"
                className="rounded border"
              />
              <i
                className={`bi bi-${displayData.platform} position-absolute`}
                style={{
                  backgroundColor: "white",
                  color: getPlatformIcon(displayData.platform).color,
                  bottom: 0,
                  right: -3,
                  borderRadius: "4px",
                }}
              ></i>
            </div>
          )}
          <div className="flex-grow-1">
            <h6
              className="mb-1 text-truncate"
              style={{
                maxWidth: "200px",
                opacity: isLiveUrlFetching ? 0.45 : 1,
                transition: "opacity 0.2s ease",
                cursor: isLiveUrlFetching ? "default" : "pointer",
                pointerEvents: isLiveUrlFetching ? "none" : "auto",
              }}
              onClick={
                displayData?.status !== 10
                  ? handleRedirect
                  : liveRedirectUrl
                    ? () =>
                        window.open(
                          liveRedirectUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                    : handleRouting
              }
            >
              {displayData.channel_name || displayData.title}
              {isLiveUrlFetching && (
                <span
                  className="spinner-border ms-1"
                  style={{
                    width: "0.6rem",
                    height: "0.6rem",
                    borderWidth: "1.5px",
                    verticalAlign: "middle",
                  }}
                  role="status"
                />
              )}
            </h6>
            <div className="">
              <div className="d-flex justify-content-between">
                <div className="gap-2 d-flex">
                  <Tooltip
                    content={
                      displayData?.schedule &&
                      displayData.schedule >= Date.now() &&
                      displayData.status === 10
                        ? `SCHEDULED ON: ${new Date(
                            displayData.schedule
                          ).toLocaleString()}`
                        : displayData?.status === 10
                          ? `PUBLISHED ON: ${formatTimestampToAgo(
                              displayData?.mt && displayData.mt > 0
                                ? displayData.mt
                                : Date.now()
                            )}`
                          : displayData.status === 0
                            ? `IN QUEUE: ${formatTimestampToAgo(
                                displayData?.ct && displayData.ct > 0
                                  ? displayData.ct
                                  : Date.now()
                              )}`
                            : displayData.status === -1 ||
                                displayData.status === -2
                              ? displayData.msg
                              : `IN PROGRESS: ${formatTimestampToAgo(
                                  displayData?.ct && displayData.ct > 0
                                    ? displayData.ct
                                    : Date.now()
                                )}`
                    }
                  >
                    <span>
                      {displayData?.schedule &&
                      displayData.schedule >= Date.now() &&
                      displayData.status === 10 ? (
                        <i
                          className="bi bi-clock"
                          style={{ fontSize: "0.8rem" }}
                        />
                      ) : (
                        renderStatusIndicator(displayData?.status ?? 1)
                      )}
                    </span>
                  </Tooltip>

                  <span className="fs-6">
                    {displayData?.schedule &&
                    displayData.schedule >= Date.now() &&
                    displayData.status === 10
                      ? `SCHEDULED ON: ${new Date(
                          displayData.schedule
                        ).toLocaleString()}`
                      : displayData?.status === 10
                        ? `PUBLISHED ON: ${formatTimestampToAgo(
                            displayData?.mt && displayData.mt > 0
                              ? displayData.mt
                              : Date.now()
                          )}`
                        : displayData.status === 0
                          ? `IN QUEUE: ${formatTimestampToAgo(
                              displayData?.ct && displayData.ct > 0
                                ? displayData.ct
                                : Date.now()
                            )}`
                          : displayData.status === -1 ||
                              displayData.status === -2
                            ? `Error while uploading: ${formatTimestampToAgo(
                                displayData?.mt && displayData.mt > 0
                                  ? displayData.mt
                                  : Date.now()
                              )}`
                            : `IN PROGRESS: ${formatTimestampToAgo(
                                displayData?.ct && displayData.ct > 0
                                  ? displayData.ct
                                  : Date.now()
                              )}`}
                  </span>
                </div>
              </div>

              <div className="mt-2">
                {displayData?.published_id && displayData.status === 10 && (
                  <Button
                    type="media-id"
                    onClick={(e) => handleCopyId(e, displayData.published_id)}
                  >
                    ID:{" "}
                    {displayData.platform === "linkedin"
                      ? displayData?.published_id.split(":")[3]
                      : displayData?.published_id}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {showActions && (
        <div className="d-flex gap-1 mt-2" style={{ flexWrap: "nowrap" }}>
          <button
            type="button"
            className="btn btn-outline-primary d-flex align-items-center gap-1"
            onClick={handleEditClick}
            style={{
              fontSize: "0.7rem",
              padding: "3px 10px",
              borderRadius: "12px",
              fontWeight: "600",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <i className="bi bi-pencil" style={{ fontSize: "0.65rem" }}></i>
            Edit
          </button>
          {displayData.status == 10 && onAddToPriority && (
            <button
              type="button"
              className="btn btn-dark d-flex align-items-center gap-1"
              onClick={(e) => handleAddToPriority(e)}
              style={{
                fontSize: "0.7rem",
                padding: "3px 10px",
                borderRadius: "12px",
                fontWeight: "600",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <i className="bi bi-list-ol" style={{ fontSize: "0.65rem" }}></i>
              Priority
            </button>
          )}
        </div>
      )}
      {/* )} */}
    </div>
  );
};

export default DenmarkPublishPopover;
